/**
 * Окно карточки Wildberries на каркасе сущности.
 *
 * Деталь карточки сводится к общей модели окна (`McardModel`), а каркас, все
 * проверки, единые вкладки, режим чтения и правка через журнал правок живут в
 * `McardWindow`. Здесь только то, что у ВБ своё: разбор детали, претензии
 * площадки, публикация и SEO-канал, СПП и индекс цен.
 *
 * Цена только для просмотра: правится в разделе «Цены». Окно целиком стоит на
 * общем модуле `mp-card`; правка текста, фото, бренда и габаритов идёт через
 * журнал правок (`windowEditApi`: ПИМ → журнал → площадка), а не прямыми
 * вызовами card-editor — тех методов в клиенте больше нет.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { useToastStore } from '../../../stores/toastStore'
import { CHECK_STATUS_META, SPP_CONFIDENCE_LABELS } from '../../price-index/types'
import { contentStatusLabels } from '../../seo/workbench'
import { SYNC_STATE_LABELS } from '../../seo-v2/lib'
import { moderationLabels } from '../../seo-v2/publish'
import { EntityRelationsPanel } from '../../entity-relations'
// Подписи состояний берём из тех же словарей, что и разделы, куда из карточки
// ведут переходы: оператор должен видеть одну и ту же формулировку здесь и там.
import { STATUS_INFO, publicationModeLabel } from '../../marketplace-catalog/draftColumns'
import { McardWindow } from '../../marketplace-catalog/components/mp-card/McardWindow'
import { Notice, Panel, ReadonlyValue, formatDateTime } from '../../marketplace-catalog/components/mp-card/McardParts'
import {
  formatRub,
  type McardCharacteristic,
  type McardIssue,
  type McardModel,
} from '../../marketplace-catalog/components/mp-card/model'
import { wildberriesAPI } from '../api'
import { nmIdOf, priceOf, vendorCodeOf, wbStatusLabel, wbStatusVariant } from './columns'
import type { WbCatalogProduct } from './types'

interface WbProductModalProps {
  sellerId: string | null
  sellerName?: string | null
  nmId: string | null
  keys: string[]
  rows: WbCatalogProduct[]
  onNavigate: (key: string) => void
  onClose: () => void
  onSync: () => void
  /** Окно открыто навигатором по цепочке: есть куда вернуться (кнопка и Alt+←). */
  canGoBack?: boolean
  onBack?: () => void
}

const photoUrlOf = (photo: any): string =>
  typeof photo === 'string'
    ? photo
    : photo?.big || photo?.c516x688 || photo?.c246x328 || photo?.square || photo?.tm || ''

const characteristicValue = (item: any): string =>
  Array.isArray(item?.value) ? item.value.join(', ') : String(item?.value ?? '')

/** Характеристики окна: справочник предмета (обязательность) + то, что стоит на витрине. */
export const wbCharacteristics = (product: WbCatalogProduct | null): McardCharacteristic[] => {
  if (!product) return []
  const specs: any[] = Array.isArray(product.characteristic_specs) ? product.characteristic_specs : []
  const current: any[] = Array.isArray(product.characteristics) ? product.characteristics : []
  const valueOf = (id: unknown, name: unknown): string => {
    const found = current.find(
      (item) => (item.id !== undefined && String(item.id) === String(id)) || (name && item.name === name),
    )
    return found ? characteristicValue(found) : ''
  }
  const rows: McardCharacteristic[] = specs.map((spec) => ({
    key: String(spec.charc_id ?? spec.name),
    targetId: Number.isFinite(Number(spec.charc_id)) ? Number(spec.charc_id) : null,
    name: String(spec.name ?? spec.charc_id),
    required: Boolean(spec.is_required),
    value: valueOf(spec.charc_id, spec.name),
    hint: `WB ID ${spec.charc_id ?? '—'}${spec.max_count ? ` · до ${spec.max_count} знач.` : ''}`,
  }))
  const known = new Set(rows.map((row) => row.key))
  for (const item of current) {
    const key = String(item.id ?? item.name)
    if (known.has(key)) continue
    rows.push({
      key,
      targetId: Number.isFinite(Number(item.id)) && item.id ? Number(item.id) : null,
      name: String(item.name || item.id),
      required: Boolean(item.required),
      value: characteristicValue(item),
      hint: `WB ID ${item.id || '—'}`,
    })
  }
  return rows
}

export const buildWbModel = (
  product: WbCatalogProduct,
  nmId: string,
  sellerName?: string | null,
  sellerId?: string | null,
): McardModel => {
  const { base, discount, final } = priceOf(product)
  const photos = (Array.isArray(product.photos) ? product.photos : []).map(photoUrlOf).filter(Boolean)
  const seo = product.seo_channel || null
  const publication = product.publication || null
  const inTrash = Boolean(product.is_in_trash)
  const issues: McardIssue[] = []
  if (seo?.moderation_message) issues.push({ level: 'warning', title: 'Модерация', text: String(seo.moderation_message) })
  if (publication?.error) issues.push({ level: 'warning', title: 'Публикация', text: String(publication.error) })
  const dimensions = product.dimensions
  const dimensionsText =
    dimensions && [dimensions.length, dimensions.width, dimensions.height].some((value) => value != null)
      ? `${dimensions.length ?? '—'} × ${dimensions.width ?? '—'} × ${dimensions.height ?? '—'} см · ${dimensions.weightBrutto ?? '—'} кг`
      : null
  const subject = product.subjectName || product.subject_name || null
  const imt = product.imtID ?? product.imt_id ?? null
  return {
    platform: 'wb',
    article: vendorCodeOf(product) || nmId,
    title: String(product.title || ''),
    description: String(product.description || ''),
    brand: product.brand ? String(product.brand) : null,
    statusLabel: wbStatusLabel(product.is_in_trash),
    statusVariant: wbStatusVariant(product.is_in_trash),
    showcase: inTrash
      ? {
          tone: 'bad',
          value: 'в корзине',
          onShowcase: false,
          problem: {
            title: 'Карточка в корзине площадки — на витрине её нет',
            hint: 'Покупатель её не видит. Восстановить можно только в кабинете Wildberries.',
          },
        }
      : { tone: 'ok', value: 'на витрине', onShowcase: true },
    photos,
    price: {
      shown: product.price?.base_price != null ? final || base : null,
      old: discount > 0 ? base : null,
      discountPercent: discount > 0 ? discount : null,
      currency: 'RUB',
    },
    stock: null,
    stockWhy: 'в зеркале ВБ нет остатков',
    issues,
    characteristics: wbCharacteristics(product),
    specsKnown: Array.isArray(product.characteristic_specs) && product.characteristic_specs.length > 0,
    lastSyncAt: (product.last_sync_at as string | undefined) ?? null,
    platformUrl: nmId ? `https://www.wildberries.ru/catalog/${nmId}/detail.aspx` : null,
    idMeta: [
      { label: 'NM', value: nmId, mono: true, copy: true },
      { label: 'IMT', value: imt != null ? String(imt) : null, mono: true, copy: true },
      { label: 'Предмет', value: subject },
    ],
    barcode: product.sizes?.[0]?.skus?.[0] ? String(product.sizes[0].skus[0]) : null,
    cabinetLabel: sellerName || (sellerId ? `#${sellerId}` : ''),
    designCode: product.design_code ? String(product.design_code) : null,
    pimArticle: product.pim_article ?? null,
    themeNumber: product.theme_number ?? null,
    dimensionsText,
    categoryName: subject,
    seoSyncState: seo?.sync_state ?? null,
    seoLastPublishedAt: seo?.last_published_at ?? null,
    bundleExternalId: imt,
  }
}

export const WbProductModal: React.FC<WbProductModalProps> = ({
  sellerId,
  sellerName,
  nmId,
  keys,
  rows,
  onNavigate,
  onClose,
  onSync,
  canGoBack,
  onBack,
}) => {
  const { showToast } = useToastStore()
  const [detail, setDetail] = useState<WbCatalogProduct | null>(null)
  const [loading, setLoading] = useState(false)
  /** Первая загрузка полной карточки закончилась (успехом или ошибкой): до этого окно показывает скелетоны. */
  const [detailSettled, setDetailSettled] = useState(false)
  /**
   * Претензии площадки к артикулу. Читаются по кнопке: у метода WB лимит
   * 10 запросов в минуту, а карточки листают стрелками.
   * null — ещё не проверяли, что не то же самое, что «претензий нет».
   */
  const [claims, setClaims] = useState<{ claim: string | null } | null>(null)
  const [claimsLoading, setClaimsLoading] = useState(false)

  const listRow = useMemo(() => rows.find((row) => nmIdOf(row) === nmId) || null, [rows, nmId])
  const product = useMemo<WbCatalogProduct | null>(
    () => (listRow || detail ? { ...(listRow || {}), ...(detail || {}) } : null),
    [listRow, detail],
  )

  const loadDetail = useCallback(
    async (refresh = false) => {
      if (!sellerId || !nmId) {
        setDetailSettled(true)
        return
      }
      setLoading(true)
      try {
        const response = refresh
          ? await wildberriesAPI.refreshProductCard(Number(sellerId), Number(nmId))
          : await wildberriesAPI.getWbProductByNmId(Number(sellerId), Number(nmId))
        const data = response.data?.data ?? response.data
        if (data && !data.error) setDetail(data)
        if (refresh) {
          showToast({
            type: 'success',
            title: 'Карточка сверена с площадкой',
            message: 'Перечитана одна карточка WB',
            duration: 3500,
          })
        }
      } catch (error: any) {
        showToast({
          type: 'error',
          title: 'Не удалось загрузить карточку',
          message: error?.response?.data?.error?.message || error?.message,
          duration: 5000,
        })
      } finally {
        setLoading(false)
        setDetailSettled(true)
      }
    },
    [nmId, sellerId, showToast],
  )

  useEffect(() => {
    setDetail(null)
    setDetailSettled(false)
    // Претензии относятся к конкретному артикулу: показать их на соседней
    // карточке было бы хуже, чем не показать вовсе.
    setClaims(null)
    void loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, nmId])

  // Соседние карточки подгружаются заранее: листание стрелками без ожидания.
  useEffect(() => {
    if (!sellerId || !nmId || !detail) return
    const index = keys.indexOf(nmId)
    for (const key of [keys[index - 1], keys[index + 1]].filter(Boolean)) {
      void wildberriesAPI.getWbProductByNmId(Number(sellerId), Number(key)).catch(() => undefined)
    }
  }, [sellerId, nmId, detail, keys])

  const loadClaims = async () => {
    if (!sellerId || !nmId) return
    setClaimsLoading(true)
    try {
      const response = await wildberriesAPI.getProductClaims(Number(sellerId), Number(nmId))
      const data = response.data?.data ?? response.data
      setClaims({ claim: data?.claim ?? null })
    } catch (error: any) {
      showToast({
        type: 'error',
        title: 'Не удалось прочитать претензии',
        message: error?.response?.data?.message || error?.message || 'Площадка не ответила',
        duration: 5000,
      })
    } finally {
      setClaimsLoading(false)
    }
  }

  const model = useMemo(
    () => (product && nmId ? buildWbModel(product, nmId, sellerName, sellerId) : null),
    [product, nmId, sellerName, sellerId],
  )

  if (!nmId) return null

  const seo = product?.seo_channel || null
  const publication = product?.publication || null
  const priceIndex = product?.price_index || null
  const { base, discount, final } = priceOf(product || {})

  const overviewExtra = (
    <>
      <Panel title="Состояние на Wildberries">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <ReadonlyValue label="Номер карточки" value={nmId} mono hint="NM ID — идентификатор площадки, не изменяется" />
          <ReadonlyValue
            label="Номер склейки"
            value={product?.imtID ?? product?.imt_id ?? null}
            mono
            why="карточка не в склейке"
            hint="IMT ID — склейка это отдельная операция WB"
          />
          <ReadonlyValue
            label="Предмет"
            value={product?.subjectName || product?.subject_name || null}
            hint={
              product?.subjectID ?? product?.subject_id
                ? `Subject ID ${product?.subjectID ?? product?.subject_id} · смена предмета — отдельная операция`
                : 'Смена предмета — отдельная операция'
            }
          />
          <ReadonlyValue label="Статус витрины" value={product ? wbStatusLabel(product.is_in_trash) : null} />
        </div>
      </Panel>
      <Panel title="Претензии Wildberries к артикулу">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-zinc-400">
            Список ведётся по кабинету и хранит отказы в создании карточек.
          </span>
          <Button size="sm" variant="secondary" loading={claimsLoading} onClick={() => void loadClaims()}>
            {claims ? 'Проверить ещё раз' : 'Проверить'}
          </Button>
        </div>
        {claims ? (
          <div
            className={
              claims.claim
                ? 'mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/30 dark:text-red-300'
                : 'mt-2 text-xs text-gray-500 dark:text-zinc-400'
            }
          >
            {claims.claim || 'Площадка не сообщала претензий по этому артикулу.'}
          </div>
        ) : (
          !claimsLoading && (
            <p className="mt-2 text-[11px] text-gray-400 dark:text-zinc-500">
              Не проверялись: у метода площадки лимит запросов, поэтому претензии читаются по кнопке.
            </p>
          )
        )}
      </Panel>
      <Panel title="Публикация и SEO-канал">
        <div className="grid gap-4 sm:grid-cols-3">
          <ReadonlyValue
            label="Статус публикации"
            value={publication?.status ? STATUS_INFO[publication.status]?.label || publication.status : null}
            why="публикаций нет"
          />
          <ReadonlyValue
            label="Режим"
            value={publication ? publicationModeLabel(publication.publication_mode) : null}
            why="публикаций нет"
          />
          <ReadonlyValue
            label="Опубликовано"
            value={publication?.published_at ? formatDateTime(publication.published_at) : null}
            why="публикаций нет"
          />
          <ReadonlyValue
            label="Контент"
            value={seo?.status ? contentStatusLabels[seo.status] || seo.status : null}
            why="SEO-контента нет"
          />
          <ReadonlyValue
            label="Синхронизация"
            value={seo?.sync_state ? SYNC_STATE_LABELS[seo.sync_state] || seo.sync_state : null}
            why="SEO-контента нет"
          />
          <ReadonlyValue
            label="Модерация"
            value={seo?.moderation_status ? moderationLabels[seo.moderation_status]?.label || seo.moderation_status : null}
            why="на модерацию не отправлялось"
          />
        </div>
        {seo?.last_published_at && (
          <p className="mt-3 text-[11px] text-gray-500 dark:text-zinc-400">
            Последняя публикация SEO: {formatDateTime(seo.last_published_at)}.
          </p>
        )}
      </Panel>
    </>
  )

  const priceContent = (
    <div className="space-y-4">
      <Panel title="Цена Wildberries">
        <div className="grid gap-4 sm:grid-cols-4">
          <ReadonlyValue label="Базовая цена" value={product?.price?.base_price != null ? formatRub(base) : null} why="цена не пришла" />
          <ReadonlyValue label="Скидка" value={product?.price?.discount != null ? `${discount} %` : null} why="скидка не пришла" />
          <ReadonlyValue label="Итоговая" value={product?.price?.base_price != null ? formatRub(final) : null} why="цена не пришла" />
          <ReadonlyValue
            label="WB Клуб"
            value={product?.price?.wb_club_discount != null ? `${product.price.wb_club_discount} %` : null}
            why="скидка клуба не пришла"
          />
        </div>
      </Panel>
      <Panel title="СПП и индекс цен">
        <div className="grid gap-4 sm:grid-cols-3">
          <ReadonlyValue
            label="СПП"
            value={priceIndex?.spp != null ? `${priceIndex.spp} %` : null}
            why="расчёта нет"
            hint={
              priceIndex?.spp_confidence
                ? `Достоверность ${SPP_CONFIDENCE_LABELS[priceIndex.spp_confidence] || priceIndex.spp_confidence}, наблюдений ${priceIndex.spp_sample_count || '—'}`
                : undefined
            }
          />
          <ReadonlyValue label="Цена аналога" value={priceIndex?.actual_analog_price != null ? formatRub(priceIndex.actual_analog_price) : null} why="расчёта нет" />
          <ReadonlyValue label="Рекомендованная цена" value={priceIndex?.new_price != null ? formatRub(priceIndex.new_price) : null} why="расчёта нет" />
          <ReadonlyValue label="Аналог" value={priceIndex?.analog_vendor_code || priceIndex?.analog_nm_id || null} mono why="расчёта нет" />
          <ReadonlyValue
            label="Статус проверки"
            value={priceIndex?.check_status ? CHECK_STATUS_META[priceIndex.check_status]?.label || priceIndex.check_status : null}
            why="расчёта нет"
          />
          <ReadonlyValue label="Проверено" value={priceIndex?.checked_at ? formatDateTime(priceIndex.checked_at) : null} why="расчёта нет" />
        </div>
        {priceIndex?.analog_url && (
          <a
            href={priceIndex.analog_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block text-xs text-indigo-600 hover:underline dark:text-indigo-400"
          >
            Открыть аналог
          </a>
        )}
      </Panel>
      <Panel title="Остатки">
        <Notice>Нет данных: в зеркале Wildberries нет остатков по складам.</Notice>
      </Panel>
    </div>
  )

  return (
    <McardWindow
      platform="wb"
      sellerId={sellerId ? Number(sellerId) : null}
      sellerName={sellerName}
      windowKey={nmId}
      model={model}
      loading={!detail && !detailSettled}
      keys={keys}
      activeKey={nmId}
      onNavigate={onNavigate}
      onClose={onClose}
      canGoBack={canGoBack}
      onBack={onBack}
      onRefresh={() => loadDetail(true)}
      refreshing={loading}
      onSync={onSync}
      onReloadDetail={() => void loadDetail()}
      linkParam="nm"
      mediaEditable
      mediaHint={
        <p className="text-xs text-gray-500 dark:text-zinc-400">
          ВБ заменит набор целиком. Удалённое CDN-изображение восстановить из карточки нельзя.
        </p>
      }
      mediaExtra={
        <Panel title="Видео">
          <ReadonlyValue
            label="Ссылка WB"
            value={product?.video || null}
            why="видео в карточке нет"
            hint="Видео в окне не изменяется"
          />
        </Panel>
      }
      priceContent={priceContent}
      overviewExtra={overviewExtra}
      relations={
        <EntityRelationsPanel
          article={product?.pim_article}
          designCode={product?.design_code}
          current={{ kind: 'wb', id: String(nmId) }}
        />
      }
      bundleExternal={{ id: model?.bundleExternalId, label: 'Номер склейки (IMT ID)' }}
      extraStats={[
        {
          label: 'Претензии',
          value: claims ? (claims.claim ? 1 : 'нет') : 'не проверялись',
          tone: claims?.claim ? 'bad' : 'dim',
          note: claimsLoading ? 'проверяем' : claims ? (claims.claim ? 'площадка ответила' : undefined) : 'по кнопке в «Обзоре»',
        },
      ]}
    />
  )
}

export default WbProductModal
